import * as React from "react";
import { styled } from "@mui/material/styles";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
  AppProvider,
  Navigation,
  Router,
  Session,
} from "@toolpad/core/AppProvider";
import {
  DashboardLayout,
  SidebarFooterProps,
  ThemeSwitcher,
} from "@toolpad/core/DashboardLayout";
import { PageContainer } from "@toolpad/core/PageContainer";
import Grid from "@mui/material/Grid2";
import logo from "../assets/companyLogo.png";
import {
 
  Stack,
  Typography,
} from "@mui/material";
import { AssignmentTurnedInOutlined, FileOpenSharp, Search } from "@mui/icons-material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StoreIcon from "@mui/icons-material/Store";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/Reduxstore";
import { useNavigate } from "react-router-dom";
import { logout } from "../store/Reducers";
import TableChartIcon from "@mui/icons-material/TableChart";
import SettingsIcon from '@mui/icons-material/Settings';
import {  getDefaultPath, hasAccess, getAccessibleMaintenance, getAccessibleServices, getAccessibleInspection, MASTER_LIST, getAccessibleBroadcast, getAccessibleTimeSheets, getAccessibleStores, getAccessibleMasters, getAccessibleLogistics } from "../const/const";
import AddAlertIcon from '@mui/icons-material/AddAlert';
import CampaignIcon from '@mui/icons-material/Campaign';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import createThemeNoVars from "@mui/material/styles/createThemeNoVars";
import { Outlet } from "react-router-dom";
import useDemoRouter from "../utils/utils";
import { RolesMaster } from "../types/assetTypes";
import ListAltIcon from "@mui/icons-material/ListAlt"; 


const demoTheme = createThemeNoVars({
  components:{
    MuiAppBar:{
      styleOverrides:{
        root:{
          backgroundColor:'#f5f5f5 !important'
          // display:"none"
        }
      }
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: 'light',
    background: {
      default: '#f5f5f5', // grey background for body
      paper: '#f5f5f5',   // grey background for surfaces like cards, dialogs
    },
  },
});

const Skeleton = styled("div")<{ height: number }>(({ theme, height }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  height,
  content: '" "',
}));

export default function Layout(props: any) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector((state: RootState) => state.auth);
  const userRoles = useSelector<RootState, RolesMaster[]>(
    state => state.auth.role
  );
  const roleNames = userRoles.map(role => role.roleName); // ["Admin", "Assets Lists Read", ...]
  const accessibleMasters = getAccessibleMasters(userRoles);


const filteredMasterList = MASTER_LIST
  .filter(section => section.role.some(r => roleNames.includes(r)))
  .map(section => ({
    ...section,
    children: section.children.filter(item =>
      item.role.some(r => roleNames.includes(r))
    ),
  }));


  const NAVIGATION = [

    {
      segment: "assetInventory",
      title: "Asset Inventory",
      icon: <ShoppingCartIcon />,
      roles: ["Assets Read", "Admin", "Assets Write"]
    },

    {
      segment: "maintenance",
      title: "Repair",
      icon: <AddAlertIcon />,
      roles: ["Admin","Report Fault Read", "Report Fault Write", "Fault Database Read","Fault Database Write"],
      children: getAccessibleMaintenance(userDetails?.role).map((maintenance) => ({
        // segment: `${maintenance.name.toLowerCase()}`, // URL-friendly segment
        segment: maintenance.name.toLowerCase().replaceAll(" ", "-"),
        title: maintenance.name, // Use maintenance.name instead of object
        icon: maintenance.icon
      })),
    },
    
    {
      segment: "services",
      title: "Services",
      icon: <SettingsIcon />,
      roles: ["Admin","New Service Read", "New Service Write", "Service Read", "Service Write", "Service Mapping Read", "Service Mapping Write", "Service Template Column Read", "Service Template Column Write", "Service Template Read", "Service Template Write"
    ],
      children: getAccessibleServices(userDetails?.role).map((services) => ({
        segment: services.name.toLowerCase().replaceAll(" ", "-"), // URL-friendly segment
        title: services.name, // Use services.name instead of object
        icon: services.icon
      })),
    },
  

    {
      segment: "inspection",
      title: "Inspection",
      icon: <AssignmentTurnedInOutlined />,
      roles: ["Admin","New Inspection Read", "New Inspection Write", "Inspection Read", "Inspection Write", "Inspection Mapping Read", "Inspection Mapping Write", "Inspection Template Column Read", "Inspection Template Column Write", "Inspection Template Read", "Inspection Template Write"],
      children: getAccessibleInspection(userDetails?.role).map((inspection) => ({
        // segment: `${inspection.name.toLowerCase()}`, // URL-friendly segment
        segment: inspection.name.toLowerCase().replaceAll(" ", "-"),
        title: inspection.name, // Use inspection.name instead of object
        icon: inspection.icon
      })),
    },
    {
      segment: "indents",
      title: "Logistics",
      icon: <LocalShippingIcon />,
      roles: ["Admin", "Logistics Write", "Logistics Read"],
    },

    {
      segment: "logistics",
      title: "Logistics List",
      icon: <ListAltIcon />, // ✅ Changed icon
      roles: [
        "Admin",
        "Logistics List Read",
        "Logistics List Write"
      ],
      children: getAccessibleLogistics(userDetails?.role)?.map((logistic) => ({
        segment: logistic.name.toLowerCase().replaceAll(" ", "-"),
        title: logistic.name,
        icon: logistic.icon
      }))
    },

    {
    segment: "stores",
    title: "Stores",
    icon: <StoreIcon />, // Replace with your preferred icon
    roles: ["Admin", "Stores Read", "Stores Write",  "Main Database Read",
    "Main Database Write",
    "Country View Read",
    "Country View Write",
    "Stock In Out Read",
    "Stock In Out Write",
    "Stock_Transaction_read",
    "Stock_Transaction_write"
  ], // Adjust roles as needed
    children: getAccessibleStores(userDetails?.role).map((store) => ({
      segment: store.name.toLowerCase().replaceAll(" ", "-"),
      title: store.name,
      icon: store.icon
    })),
    },


    {
    segment: "timeSheets",
    title: "TimeSheets",
    icon: <AccessTimeIcon />, // 🔊 Updated icon
    roles: ["Admin"],
    children: getAccessibleTimeSheets(userDetails?.role).map((timeSheets) => ({
      segment: timeSheets.name.toLowerCase().replaceAll(" ", "-"),
      title: timeSheets.name,
      icon: timeSheets.icon
    })),
    },



    {
    segment: "indentsInventory",
    title: "Indents",
    icon: <ShoppingCartIcon />,
    roles: ["Admin", "Indents Read", "Indents Write"]
  },

  
    {
    segment: "indentsTemplate",
    title: "Indents Template",
    icon: <ShoppingCartIcon />,
    roles: ["Admin","Indents Read", "Indents Write"]
  },

  {
    segment: "reporting",
    title: "Reporting",
    icon: <AssessmentIcon />, // or BarChartIcon, AnalyticsIcon, etc.
    roles: ["Admin"]
  },

    {
      segment: "fileManager",
      title: "File Manager",
      icon: <FileOpenSharp />,
      roles: ["Admin"]
    },

    {
    segment: "broadcast",
    title: "Broadcast",
    icon: <CampaignIcon />, // 🔊 Updated icon
    roles: ["Admin", "Broadcast Read", "Boradcast Write"],
    children: getAccessibleBroadcast(userDetails?.role).map((broadcast) => ({
      segment: broadcast.name.toLowerCase().replaceAll(" ", "-"),
      title: broadcast.name,
      icon: broadcast.icon
    })),
    },
  {
    segment: "planner",
    title: "Resource Planner", 
    icon: <CalendarMonthIcon />,
    roles: ["Admin"],
    external: true,
    
  },

  {
    segment: "masters",
    title: "Master List",
    icon: <TableChartIcon />,
    roles: Array.from(new Set(accessibleMasters.flatMap(s => s.role))),
    children: accessibleMasters.map(section => ({
      segment: section.name.toLowerCase().replaceAll(" ", "-"),
      title: section.name,
      icon: section.icon,
      children: section.children.map(item => ({
        segment: item.name.toLowerCase(),
        title: item.name.replaceAll("_", " "),
        icon: item.icon,
      })),
    })),
  },
  {
    segment: "userhistory",
    title: "User History",
    icon: <ShoppingCartIcon />,
    roles: ["Admin",  "User History Read", "User History Write"      
  ]
  },

  ];



  const { window,

  } = props;

  const navigateTo = useNavigate();
  const { router, setPathname } = useDemoRouter(getDefaultPath(userDetails?.role.map((role) => role.roleName)), navigateTo);
  const [session, setSession] = React.useState<Session | null>({
    user: {
      name: userDetails.name || "test",
      email: userDetails.email || "development@outlook.com",
      image: "", // Use URL to user badge icon
    },
  });

  // Remove this const when copying and pasting into your project.
  const demoWindow = window ? window() : undefined;
  console.log(router.pathname,"router.pathname");

  const authentication = React.useMemo(() => {
    return {
      
      signIn: () => {
        setSession({
          user: {
            name: userDetails.name || "test",
            email: userDetails.email || "development@outlook.com",
            image: "", // Use URL to user badge icon
          },
        });
      },
      signOut: () => {
        setSession(null);
        // navigate("/");
        dispatch(logout());
      },
    };
  }, []);

  function SidebarFooter({ mini }: SidebarFooterProps) {
    return (
      <Typography
        variant="caption"
        sx={{ m: 1, whiteSpace: "nowrap", overflow: "hidden" }}
      >
        {/* {mini ? "© ERP" : `© ${new Date().getFullYear()} ERP`} */}
        {`© ${new Date().getFullYear()}`}
      </Typography>
      // <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px" }}>
      //   <Typography
      //     variant="caption"
      //     sx={{ m: 1, whiteSpace: "nowrap", overflow: "hidden" }}
      //   >
      //     {session ? `${session.user?.name ?? ""} - ${session.user?.email ?? ""}` : "Not signed in"}
      //   </Typography>
  
      //   {session && (
      //     <ReusablePrimeButton
      //       title="Sign Out"
      //       icon="pi pi-sign-out"
      //       size="small"
      //       style={{ marginTop: 1 }}
      //       label="Sign Out"
      //       severity="info"
      //       onClick={authentication.signOut} // Calls the sign-out function
      //     />
      //   )}
      // </div>
    );
  }

  function ToolbarActionsSearch() {
    return (
      <Stack direction="row">
        {/* <Tooltip title="Search" enterDelay={1000}>
            <div>
            <IconButton
                 ="button"
                aria-label="search"
                sx={{
                display: { xs: 'inline', md: 'none' },
                }}
            >
                <Search />
            </IconButton>
            </div>
        </Tooltip>
        <TextField
            label="Search"
            variant="outlined"
            size="small"
            slotProps={{
            input: {
                endAdornment: (
                <IconButton  ="button" aria-label="search" size="small">
                    <Search />
                </IconButton>
                ),
                sx: { pr: 0.5 },
            },
            }}
            sx={{ display: { xs: 'none', md: 'inline-block' }, mr: 1 }}
        /> */}
        {/* <ThemeSwitcher /> */}
      </Stack>
    );
  }
// Filter navigation items based on user roles
const filteredNavigation:any = NAVIGATION.filter(
  (item) =>
    !item.roles || hasAccess(item.roles, userDetails.role) // Show item if no roles defined or user has access
);



const [isSidebarCollapsed, setSidebarCollapsed] = React.useState(true);



React.useEffect(() => {
  if (typeof window !== "undefined" && router.pathname === "/planner") {
    const newWindow = window.open("https://google.com", "_blank", "noopener,noreferrer");
    if (newWindow) {
      newWindow.focus();
    }
  }
}, [router.pathname]);

console.log("col", isSidebarCollapsed);
  return (
    <AppProvider
      session={session}
      navigation={filteredNavigation}
      router={router}
      theme={demoTheme}
      authentication={authentication}
      window={demoWindow}
      branding={{
        logo: <img src={logo} alt="Company Logo" />,
        title: isSidebarCollapsed ? "Asset Management" : "Asset Management",
        homeUrl: "#",
      }}
    >
        <DashboardLayout
          navigation={filteredNavigation}
          onSidebarToggle={setSidebarCollapsed}
          slots={{
            toolbarActions: ToolbarActionsSearch,
            sidebarFooter: SidebarFooter,
          }}
          defaultSidebarCollapsed
          hideBreadcrumbs
        >
        <PageContainer title="" sx={{ backgroundColor: "#f5f5f5 !important" } } >
          <Outlet />
        </PageContainer>
      </DashboardLayout>


    </AppProvider>
  );
  
}
